import Button from '@/components/Button';
import EmptyStateNotice from '@/components/EmptyStateNotice';
import KeyHandler from '@/components/KeyHandler';
import DetailsSearch from '@/components/modals/workflowLogsModal/DetailsSearch';
import Page from '@/components/page/Page';
import { FiArrowLeft, FiArrowRight, MdOutlineDashboardCustomize } from '@/components/react-icons/main';
import ReactSlider from '@/components/ReactSlider';
import DividerV2 from '@/components/v2/DividerV2';
import ScrollContainerV2 from '@/components/v2/ScrollContainerV2';
import { useAnalytics } from '@/hooks/useAnalytics';
import setQuery from '@/lib/setQuery';
import { cancelWorkflow } from '@/redux/actions/gravityActions';
import { useArgSelector } from '@/redux/curriedSelectors/curriedSelectors';
import { query } from '@/redux/selectors/querySelectors';
import selectors from '@/redux/selectors/selectors';
import LogOperationLogsPopover from '@/screens/workflows/workflowLogs/LogOperationLogsPopover';
import { useEffect, useMemo, useRef } from 'react';
import useLogStepsContextMenu from '../hooks/useLogStepsContextMenu';
import getLogTimeMachine from './lib/getLogTimeMachine';
import getReplayRangeModel, { findSpanContaining } from './lib/getReplayRangeModel';
import LogStep from './LogStep';

const LogSteps = ({ loading = false, invokedBy, manualUser, manualUserId }) => {
  const workflowId = useArgSelector(selectors.workflowId);
  const search = useArgSelector(query, 'detailsSearch');
  const activeLogId = useArgSelector(selectors.query, 'logId');
  const activeStepId = useArgSelector(query, 'logStepId');
  const time = useArgSelector(query, 'time');
  const replayMode = useArgSelector(query, 'replayMode');
  const replayStart = useArgSelector(query, 'replayStart');
  const replayEnd = useArgSelector(query, 'replayEnd');
  const scrollTo = useArgSelector(query, 'scrollTo');
  const output = useArgSelector(selectors.workflowLogOutputById, activeLogId);
  const activeLog = useArgSelector(selectors.workflowLogsById, activeLogId);
  const operationLogs = useArgSelector(selectors.workflowOperationLogsByLogId, activeLogId);
  const isPointerDownRef = useRef(false);
  const lastSearch = useRef(null);
  const { trackEvent } = useAnalytics();
  const triggerStep = activeLog?.meta?.triggerStep;
  const isWorkflowEmpty = activeLog?.meta?.isWorkflowEmpty;
  const isRunning = activeLog?.startedOn && !activeLog?.finishedOn && activeLog?.statusId === 1;
  const isCrashed = activeLog?.statusId === 5;
  const isCancelled = activeLog?.statusId === 4;

  const memoizedTimeMachine = useMemo(() => {
    return getLogTimeMachine({ output, operationLogs, time, search, triggerStep, invokedBy, manualUser, manualUserId, incomplete: isCancelled || isCrashed });
  }, [output, operationLogs, time, search, triggerStep, invokedBy, manualUser, manualUserId, isCancelled, isCrashed]);

  const { timeMachine, allSteps, searchTime } = memoizedTimeMachine;

  const replayModel = useMemo(() => {
    return getReplayRangeModel({ allSteps, timeMachine, steps: output?.meta?.steps });
  }, [allSteps, timeMachine, output?.meta?.steps]);

  const isReplayMode = replayMode === 'true' && !isRunning && replayModel.spans.length > 0;
  const rangeStart = Number.isFinite(parseInt(replayStart, 10)) ? parseInt(replayStart, 10) : replayModel.min;
  const rangeEnd = Number.isFinite(parseInt(replayEnd, 10)) ? parseInt(replayEnd, 10) : replayModel.max;
  const startSpan = isReplayMode ? findSpanContaining(replayModel.spans, rangeStart) : undefined;
  const endSpan = isReplayMode ? findSpanContaining(replayModel.spans, rangeEnd) : undefined;

  const noOutputTitle = isRunning ? 'No logs are available until workflow is finished' : 'No logs are available';
  const noOutputDescription = isCrashed ? 'This is due to the workflow crashing before logs could be uploaded.' : isCancelled ? 'This is due to the user cancelling the workflow before logs could be uploaded.' : 'No output available for this log';

  useLogStepsContextMenu({ allSteps, replayModel, activeLog });

  const handleNavigationKeypress = () => {
    if (!['INPUT', 'SELECT'].includes(document.activeElement?.nodeName)) {
      document.querySelector('.log-steps-slider-component .thumb')?.focus();
    }
  };

  const handleCancelClick = () => {
    trackEvent('workflow-cancel-log');
    cancelWorkflow({ workflowId, workflowLogId: activeLogId });
  };

  const handleSliderChange = (newTime) => {
    const effectiveStepId = activeStepId || allSteps?.[0]?.stepId;

    if (!effectiveStepId || !allSteps?.length) {
      console.log('No effective step ID or steps, returning early');
      return;
    }

    const step = allSteps?.find((item) => item.stepId === effectiveStepId);
    const timesWithResult = step?.searchableData?.map((s) => s.time) || [];

    const queryUpdate = { time: newTime, logStepId: effectiveStepId };

    if (isPointerDownRef.current) {
      setQuery(queryUpdate);
      return;
    }

    if (timesWithResult.length > 0) {
      const findNearestTime = (times, target, findGreater) => {
        const sortedTimes = findGreater ? times : [...times].reverse();
        return sortedTimes.find((t) => (findGreater ? t > target : t < target)) || target;
      };

      const updatedTime = newTime > time ? findNearestTime(timesWithResult, time, true) : findNearestTime(timesWithResult, time, false);
      queryUpdate.time = updatedTime;
    }

    setQuery(queryUpdate);
  };

  const currentTime = parseFloat(time || 0);
  const maxTime = Math.max(timeMachine?.length - 1 || 0, 1);

  // Range thumbs snap to whole top-level actions, so a drag into a loop body
  // always selects the loop's entire span.
  const handleRangeChange = ([rawStart, rawEnd]) => {
    const nextStartSpan = findSpanContaining(replayModel.spans, rawStart);
    const nextEndSpan = findSpanContaining(replayModel.spans, rawEnd);
    if (!nextStartSpan || !nextEndSpan) {
      return;
    }
    setQuery({ replayStart: nextStartSpan.start, replayEnd: Math.max(nextEndSpan.end, nextStartSpan.end) });
  };

  const handleSetRangeStart = (span) => {
    setQuery({ replayStart: span.start, replayEnd: Math.max(rangeEnd, span.end) });
  };

  const handleSetRangeEnd = (span) => {
    setQuery({ replayEnd: span.end, replayStart: Math.min(rangeStart, span.start) });
  };

  const handlePrevious = () => {
    if (currentTime <= 0) {
      return;
    }
    const effectiveStepId = activeStepId || allSteps?.[0]?.stepId;
    setQuery({ time: currentTime - 1, logStepId: effectiveStepId });
  };

  const handleNext = () => {
    if (currentTime >= maxTime) {
      return;
    }
    const effectiveStepId = activeStepId || allSteps?.[0]?.stepId;
    setQuery({ time: currentTime + 1, logStepId: effectiveStepId });
  };

  const handlePreviousKeypress = () => {
    const activeElement = document.activeElement;
    if (activeElement && ['INPUT', 'TEXTAREA'].includes(activeElement.nodeName)) {
      return;
    }
    handlePrevious();
  };

  const handleNextKeypress = () => {
    const activeElement = document.activeElement;
    if (activeElement && ['INPUT', 'TEXTAREA'].includes(activeElement.nodeName)) {
      return;
    }
    handleNext();
  };

  const windowKeyMap = {
    ArrowLeft: handlePreviousKeypress,
    ArrowRight: handleNextKeypress,
    ArrowUp: handleNavigationKeypress,
    ArrowDown: handleNavigationKeypress,
    PageUp: handleNavigationKeypress,
    PageDown: handleNavigationKeypress,
    Home: handleNavigationKeypress,
    End: handleNavigationKeypress
  };

  useEffect(() => {
    if (isReplayMode && (replayStart === undefined || replayStart === null || replayEnd === undefined || replayEnd === null)) {
      setQuery({ replayStart: replayModel.min, replayEnd: replayModel.max });
    }
  }, [isReplayMode, replayStart, replayEnd, replayModel.min, replayModel.max]);

  useEffect(() => {
    if (allSteps?.length > 0) {
      const queryUpdates = {};

      if (!activeStepId) {
        queryUpdates.logStepId = allSteps[0]?.stepId;
      }

      if (time === undefined || time === null) {
        queryUpdates.time = 0;
        console.log('Setting time to: 0');
      }

      if (Object.keys(queryUpdates).length > 0) {
        setTimeout(() => {
          setQuery(queryUpdates);
        }, 0);
      }
    }
  }, [allSteps?.length, activeStepId, time, workflowId]);

  useEffect(() => {
    if (scrollTo) {
      const selectedElement = document.querySelector(`[data-step-id="${scrollTo}"]`);
      const scrollContainer = document.querySelector('.comp-scroll-container-v2.log-steps-wrapper');

      if (selectedElement && scrollContainer) {
        const containerRect = scrollContainer.getBoundingClientRect();
        const elementRect = selectedElement.getBoundingClientRect();

        const scrollTop = selectedElement.offsetTop - containerRect.height / 2 + elementRect.height / 2;

        scrollContainer.scrollTo({
          top: scrollTop,
          behavior: 'instant'
        });

        setQuery({ scrollTo: null });
      }
    }
  }, [scrollTo]);

  useEffect(() => {
    if (!!search && parseFloat(searchTime) !== parseFloat(time) && lastSearch.current !== search) {
      setQuery({ time: searchTime || 0, logTab: 'timeline', logStepId: allSteps?.[0]?.stepId });
    }

    lastSearch.current = search;
  }, [search, searchTime, time, workflowId]);

  useEffect(() => {
    const handlePointerDown = () => {
      isPointerDownRef.current = true;
    };

    const handlePointerUp = () => {
      isPointerDownRef.current = false;
    };

    const slider = document.querySelector('.log-steps-slider-component');

    if (slider) {
      slider.addEventListener('pointerdown', handlePointerDown, true);
    }
    window.addEventListener('pointerup', handlePointerUp, true);
    window.addEventListener('pointercancel', handlePointerUp, true);

    return () => {
      if (slider) {
        slider.removeEventListener('pointerdown', handlePointerDown, true);
      }
      window.removeEventListener('pointerup', handlePointerUp, true);
      window.removeEventListener('pointercancel', handlePointerUp, true);
    };
  }, [timeMachine?.length]);

  if (loading) {
    return (
      <Page.Section className="log-steps-wrapper-loading">
        <EmptyStateNotice size={'sm'} loading card title={'Loading log...'} />
      </Page.Section>
    );
  }

  if (!output || isRunning) {
    return <EmptyStateNotice icon={MdOutlineDashboardCustomize} size={'sm'} title={noOutputTitle} description={noOutputDescription} actions={isRunning ? [{ label: 'Cancel Workflow', onClick: handleCancelClick }] : []} />;
  }

  return (
    <ScrollContainerV2 className={'log-steps-wrapper'}>
      <div className={'log-steps-header'}>
        <h3>Timeline</h3>
        <LogOperationLogsPopover />
      </div>
      <DetailsSearch />
      <DividerV2 />
      <div className={'log-steps-slider'}>
        {isReplayMode ? (
          <>
            <div className="log-slider-controls">
              <ReactSlider className={'log-steps-slider-component replay-range'} value={[rangeStart, rangeEnd]} min={replayModel.min} max={replayModel.max} onChange={handleRangeChange} pearling />
            </div>
            <div className="slider-labels">
              <span>{`Action ${replayModel.min}`}</span>
              <span className="replay-selection">{`Actions ${rangeStart} – ${rangeEnd} selected`}</span>
              <span>{`Action ${replayModel.max}`}</span>
            </div>
          </>
        ) : (
          <>
            <div className="log-slider-controls">
              <Button size="xs" className="ghost" icon={FiArrowLeft} onClick={handlePrevious} disabled={currentTime <= 0} />
              <ReactSlider className={'log-steps-slider-component'} value={currentTime} min={0} max={maxTime} onChange={handleSliderChange} />
              <Button size="xs" className="ghost" icon={FiArrowRight} onClick={handleNext} disabled={currentTime >= maxTime} />
            </div>
            <div className="slider-labels">
              <span>Action 0</span>
              <span>{`Action ${timeMachine?.length ? timeMachine.length - 1 : 1}`}</span>
            </div>
          </>
        )}
      </div>
      <div className={'log-steps'}>
        {allSteps?.map((n, idx) => {
          let replay = null;
          if (isReplayMode) {
            const ownSpan = replayModel.spans.find((span) => span.stepId === n.stepId);
            const containingSpan = ownSpan || (typeof n.firstTime === 'number' ? findSpanContaining(replayModel.spans, n.firstTime) : undefined);
            replay = {
              active: true,
              inRange: !!containingSpan && containingSpan.start <= rangeEnd && containingSpan.end >= rangeStart,
              isStart: !!ownSpan && ownSpan.stepId === startSpan?.stepId,
              isEnd: !!ownSpan && ownSpan.stepId === endSpan?.stepId,
              canSetBounds: !!ownSpan,
              onSetStart: ownSpan ? () => handleSetRangeStart(ownSpan) : undefined,
              onSetEnd: ownSpan ? () => handleSetRangeEnd(ownSpan) : undefined
            };
          }
          return <LogStep key={idx} stepId={n.stepId} timeMachine={timeMachine} meta={n} output={n?.lastOutput} indent={n?.indent} steps={output?.meta?.steps} replay={replay} />;
        })}
        {!allSteps?.length && <div className={'empty-container'}>No Results</div>}
        {isWorkflowEmpty && (
          <EmptyStateNotice //
            icon={MdOutlineDashboardCustomize}
            size={'sm'}
            className="workflow"
            title={'No steps to run yet'}
            description={'This workflow ran but it had no steps. Add a step first, then run it again.'}
          />
        )}
      </div>
      <KeyHandler keyMap={windowKeyMap} global={true} />
    </ScrollContainerV2>
  );
};

export default LogSteps;
